import React, { useState, useEffect } from 'react';
import { Database, Globe, Zap, X, Server, CheckCircle2, AlertCircle, Info, ExternalLink, Key, Map, Plus, Trash2 } from 'lucide-react';
import { toast } from '../components/Toast';
import { Environment, ConnectionStatus, ConnectionConfig } from '../types';
import { checkApiHealth } from '../services/apiService';

interface SettingsProps {
  activeEnv: Environment | null;
  onUpdateConnection: (envId: string, key: keyof ConnectionStatus, config: ConnectionConfig) => void;
}

const Settings: React.FC<SettingsProps> = ({ activeEnv, onUpdateConnection }) => {
  const [configuring, setConfiguring] = useState<keyof ConnectionStatus | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [serviceAccountFile, setServiceAccountFile] = useState<File | null>(null);
  const [serviceAccountJson, setServiceAccountJson] = useState<string>('');
  const [showHelp, setShowHelp] = useState(false);
  const [apiStatus, setApiStatus] = useState<{ ok: boolean; loading: boolean; error: string | null }>({
    ok: false,
    loading: true,
    error: null
  });

  // Sitemap URLs state
  const [sitemapUrls, setSitemapUrls] = useState<string[]>([]);
  const [newSitemapUrl, setNewSitemapUrl] = useState('');
  const [sitemapLoading, setSitemapLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const verifyBackend = async () => {
      const result = await checkApiHealth();
      if (!mounted) return;
      setApiStatus({
        ok: result.ok,
        loading: false,
        error: result.error ? result.error.message : null
      });
    };
    verifyBackend();
    return () => { mounted = false; };
  }, []);

  // Load sitemap URLs from server when environment changes
  useEffect(() => {
    if (!activeEnv) {
      setSitemapUrls([]);
      return;
    }
    const loadSitemaps = async () => {
      try {
        const response = await fetch(`/api/sitemaps?environmentId=${activeEnv.id}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSitemapUrls(data.sitemaps || []);
        }
      } catch (error) {
        // Fall back to localStorage
        const stored = localStorage.getItem(`sitemaps_${activeEnv.id}`);
        if (stored) setSitemapUrls(JSON.parse(stored));
      }
    };
    loadSitemaps();
  }, [activeEnv?.id]);

  const saveSitemaps = async (urls: string[]) => {
    setSitemapUrls(urls);
    if (!activeEnv) return;

    // Always persist to localStorage as fallback
    localStorage.setItem(`sitemaps_${activeEnv.id}`, JSON.stringify(urls));

    try {
      await fetch('/api/sitemaps', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environmentId: activeEnv.id, sitemaps: urls })
      });
    } catch (error) {
      // localStorage fallback already saved
    }
  };

  const handleAddSitemap = () => {
    const url = newSitemapUrl.trim();
    if (!url) return;
    if (!url.startsWith('https://') && !url.startsWith('http://')) {
      toast.error('Sitemap URL must start with http:// or https://');
      return;
    }
    if (!url.endsWith('.xml')) {
      toast.warning('Sitemap URLs typically end with .xml (e.g. sitemap.xml)');
    }
    if (sitemapUrls.includes(url)) {
      toast.warning('This sitemap URL is already added.');
      return;
    }
    const updated = [...sitemapUrls, url];
    saveSitemaps(updated);
    setNewSitemapUrl('');
    toast.success('Sitemap added.');
  };

  const handleRemoveSitemap = (url: string) => {
    const updated = sitemapUrls.filter(u => u !== url);
    saveSitemaps(updated);
    toast.success('Sitemap removed.');
  };

  const standardConnectors: { key: keyof ConnectionStatus; name: string; icon: any; help: string; setupSteps: string[]; example: string; link: string }[] = [
    {
      key: 'ga4',
      name: 'Google Analytics 4',
      icon: Database,
      help: 'Connect your GA4 property to pull real analytics data (sessions, pageviews, conversions, etc.)',
      setupSteps: [
        'Open Google Analytics (analytics.google.com)',
        'Click Admin (gear icon) in the bottom left',
        'Under Property, click Data Streams',
        'Click on your web stream and copy the Measurement ID (starts with G-)',
        'For API access: Go to Google Cloud Console → IAM & Admin → Service Accounts',
        'Create a Service Account, grant "Viewer" role, download JSON key',
        'In GA4 Admin, add the service account email with Viewer permissions',
        'Upload the JSON file below to enable data sync'
      ],
      example: 'G-ABC1234DEF',
      link: 'https://analytics.google.com/analytics/web/'
    },
    {
      key: 'searchConsole',
      name: 'Google Search Console',
      icon: Globe,
      help: 'Connect Search Console for SEO insights, keyword rankings, and search performance data',
      setupSteps: [
        'Open Google Search Console (search.google.com/search-console)',
        'Select your property',
        'Copy the full Property URL exactly as shown',
        'Paste it below (must include https://)',
        'Your Google account must have Owner/Full permissions'
      ],
      example: 'https://example.com',
      link: 'https://search.google.com/search-console'
    },
    {
      key: 'zapier',
      name: 'Zapier Automation',
      icon: Zap,
      help: 'Set up automated notifications when campaigns launch or content is published',
      setupSteps: [
        'Go to zapier.com and create an account',
        'Click "Create Zap"',
        'For the trigger, search "Webhooks by Zapier"',
        'Choose "Catch Hook"',
        'Copy the webhook URL provided',
        'Paste it below'
      ],
      example: 'https://hooks.zapier.com/hooks/catch/123456/abcdef/',
      link: 'https://zapier.com/app/zaps'
    }
  ];

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setServiceAccountFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = event.target?.result as string;
        JSON.parse(json);
        setServiceAccountJson(json);
      } catch (error) {
        toast.error('Invalid JSON file. Please upload a valid Service Account JSON file.');
        setServiceAccountFile(null);
        setServiceAccountJson('');
      }
    };
    reader.readAsText(file);
  };

  const handleConnect = () => {
    if (!activeEnv || !configuring || !apiKeyInput.trim()) return;

    let isValid = true;
    let errorMessage = '';

    if (configuring === 'ga4') {
      if (!/^G-[A-Z0-9]+$/.test(apiKeyInput.trim())) {
        isValid = false;
        errorMessage = 'Invalid GA4 Measurement ID format. Must start with "G-" followed by letters/numbers (e.g., G-ABC1234DEF)';
      }
    } else if (configuring === 'searchConsole') {
      if (!apiKeyInput.trim().startsWith('https://')) {
        isValid = false;
        errorMessage = 'Property URL must start with https://';
      }
    } else if (configuring === 'zapier') {
      if (!apiKeyInput.trim().startsWith('https://hooks.zapier.com/')) {
        isValid = false;
        errorMessage = 'Invalid Zapier webhook URL. Must start with https://hooks.zapier.com/';
      }
    }

    if (!isValid) {
      toast.error(errorMessage);
      return;
    }

    const config: ConnectionConfig = {
      apiKey: apiKeyInput.trim(),
      connected: true
    };

    if (configuring === 'ga4' && serviceAccountJson) {
      config.configJson = JSON.stringify({ serviceAccount: serviceAccountJson });
    }

    onUpdateConnection(activeEnv.id, configuring, config);
    setConfiguring(null);
    setApiKeyInput('');
    setServiceAccountFile(null);
    setServiceAccountJson('');
    setShowHelp(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-2 duration-700 pb-20">
      <div className="border-b border-slate-200 pb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-4xl font-black text-slate-900 tracking-tight">Settings</h2>
          <p className="text-slate-500 mt-3 text-md">Configure your data sources, sitemaps, and automation tools.</p>
        </div>

        <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold border transition-colors ${
          apiStatus.loading ? 'bg-slate-50 text-slate-400' :
          apiStatus.ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'
        }`}>
          <Server size={14} className={apiStatus.loading ? 'animate-pulse' : ''} />
          {apiStatus.loading ? 'Checking Status...' : apiStatus.ok ? 'API: Connected' : 'API: Limited Access'}
          {!apiStatus.loading && (apiStatus.ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} title={apiStatus.error || 'Connection limited'} />)}
        </div>
      </div>

      {/* Sitemap URLs Section */}
      <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
          <div className="flex items-center gap-3">
            <Map size={20} className="text-blue-500" />
            <div>
              <h3 className="font-black text-xl text-slate-900">Sitemap URLs</h3>
              <p className="text-xs text-slate-500 mt-0.5">Add your Yoast or other XML sitemap URLs for automatic site monitoring.</p>
            </div>
          </div>
        </div>
        {!activeEnv ? (
          <div className="p-12 text-center text-slate-400 font-bold italic flex flex-col items-center gap-4">
            <Map size={40} className="opacity-20" />
            Select an environment in the sidebar to manage sitemaps.
          </div>
        ) : (
          <div className="p-8 space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newSitemapUrl}
                onChange={(e) => setNewSitemapUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddSitemap(); }}
                placeholder="https://example.com/sitemap_index.xml"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-blue-400 outline-none transition-all"
              />
              <button
                onClick={handleAddSitemap}
                className="px-5 py-3 bg-blue-500 text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition-all flex items-center gap-2"
              >
                <Plus size={16} />
                Add
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-800 leading-relaxed">
                <strong>Tip:</strong> For Yoast SEO, your sitemap is usually at <code className="bg-white px-1.5 py-0.5 rounded text-blue-900">yoursite.com/sitemap_index.xml</code>.
                You can also add individual sitemaps like <code className="bg-white px-1.5 py-0.5 rounded text-blue-900">post-sitemap.xml</code> or <code className="bg-white px-1.5 py-0.5 rounded text-blue-900">page-sitemap.xml</code>.
                These sitemaps will be used by Site Monitor for auto-audits and the "Load from Sitemap" feature.
              </p>
            </div>

            {sitemapUrls.length > 0 ? (
              <div className="space-y-2">
                {sitemapUrls.map((url) => (
                  <div key={url} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 group">
                    <Map size={14} className="text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-mono text-slate-700 flex-1 truncate">{url}</span>
                    <button
                      onClick={() => handleRemoveSitemap(url)}
                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      title="Remove sitemap"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic text-center py-4">No sitemaps configured yet. Add your first sitemap URL above.</p>
            )}
          </div>
        )}
      </section>

      {/* Connections Section */}
      <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
          <h3 className="font-black text-xl text-slate-900">Data Connections</h3>
        </div>
        {!activeEnv ? (
          <div className="p-12 text-center text-slate-400 font-bold italic flex flex-col items-center gap-4">
            <Database size={40} className="opacity-20" />
            Select an environment in the sidebar to configure connections.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {standardConnectors.map((c) => (
              <div key={c.key} className="p-8 flex flex-col md:flex-row md:items-center justify-between hover:bg-slate-50/50 transition-all gap-6">
                <div className="flex items-center gap-6">
                  <div className={`p-4 rounded-2xl border transition-all ${activeEnv.connections[c.key].connected ? 'bg-green-50 text-green-600 border-green-100' : 'bg-slate-50 text-slate-400 border-slate-100'}`}>
                    <c.icon size={28} />
                  </div>
                  <div>
                    <h4 className="font-black text-lg text-slate-900">{c.name}</h4>
                    <p className="text-xs text-slate-500">{activeEnv.connections[c.key].connected ? 'Connected' : 'Not connected'}</p>
                  </div>
                </div>
                <button
                  disabled={activeEnv.isSample}
                  onClick={() => setConfiguring(c.key)}
                  className="px-6 py-2.5 bg-blue-500 text-white rounded-xl font-black text-xs hover:scale-105 transition-all disabled:opacity-50 shadow-md"
                >
                  {activeEnv.connections[c.key].connected ? 'Update' : 'Connect'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {configuring && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => { setConfiguring(null); setShowHelp(false); }}>
          <div className="bg-white rounded-3xl p-8 w-full max-w-lg shadow-2xl relative overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-black text-slate-900">Connect</h2>
                <p className="text-xs font-bold text-blue-500 uppercase tracking-widest mt-1">{standardConnectors.find(c => c.key === configuring)?.name}</p>
              </div>
              <button onClick={() => { setConfiguring(null); setShowHelp(false); }} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={20}/></button>
            </div>

            <div className="space-y-6 relative z-10">
               <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                 <h4 className="font-bold text-sm text-blue-900 mb-3 flex items-center gap-2">
                   <Info size={16} />
                   Setup Instructions
                 </h4>
                 <p className="text-xs text-blue-800 mb-3">{standardConnectors.find(c => c.key === configuring)?.help}</p>
                 <ol className="space-y-2 text-xs text-blue-900">
                   {standardConnectors.find(c => c.key === configuring)?.setupSteps.map((step, idx) => (
                     <li key={idx} className="flex gap-2">
                       <span className="font-bold min-w-[20px]">{idx + 1}.</span>
                       <span>{step}</span>
                     </li>
                   ))}
                 </ol>
                 <div className="mt-4 p-3 bg-white rounded-lg">
                   <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">Example Format:</p>
                   <code className="text-xs text-blue-900 font-mono">{standardConnectors.find(c => c.key === configuring)?.example}</code>
                 </div>
                 <a
                   href={standardConnectors.find(c => c.key === configuring)?.link}
                   target="_blank"
                   rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-blue-700 hover:text-blue-900 underline"
                 >
                   Open {standardConnectors.find(c => c.key === configuring)?.name} <ExternalLink size={12} />
                 </a>
               </div>

               <div className="relative z-20">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                   {configuring === 'ga4' && 'GA4 Measurement ID *'}
                   {configuring === 'searchConsole' && 'Property URL *'}
                   {configuring === 'zapier' && 'Webhook URL *'}
                 </label>
                 <div className="relative">
                   <input
                     type="text"
                     value={apiKeyInput}
                     onChange={(e) => setApiKeyInput(e.target.value)}
                     autoFocus
                     className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-mono focus:ring-2 focus:ring-blue-400 outline-none transition-all pr-12"
                     placeholder={configuring === 'ga4' ? 'G-ABC1234DEF' : configuring === 'searchConsole' ? 'https://example.com' : 'https://hooks.zapier.com/hooks/catch/...'}
                   />
                   <Database size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                 </div>
               </div>

               {configuring === 'ga4' && (
                 <div>
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                     Service Account JSON (Optional - for data sync)
                   </label>
                   <div className="relative">
                     <input
                       type="file"
                       accept=".json"
                       onChange={handleFileUpload}
                       className="hidden"
                       id="service-account-upload"
                     />
                     <label
                       htmlFor="service-account-upload"
                       className="w-full bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl px-4 py-6 text-sm flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 hover:border-blue-400 transition-all"
                     >
                       <Key size={24} className="text-slate-400 mb-2" />
                       {serviceAccountFile ? (
                         <div className="text-center">
                           <p className="text-xs font-bold text-green-600 flex items-center gap-1">
                             <CheckCircle2 size={14} />
                             {serviceAccountFile.name}
                           </p>
                           <p className="text-[10px] text-slate-500 mt-1">Click to replace</p>
                         </div>
                       ) : (
                         <div className="text-center">
                           <p className="text-xs font-bold text-slate-600">Upload Service Account JSON</p>
                           <p className="text-[10px] text-slate-500 mt-1">Click to browse or drag & drop</p>
                         </div>
                       )}
                     </label>
                   </div>
                   <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                     <p className="text-[10px] text-amber-800 leading-relaxed">
                       <strong>Note:</strong> Without a Service Account, you'll see sample data. To fetch real GA4 data, you must upload the Service Account JSON file with Analytics Data API access enabled.
                     </p>
                   </div>
                 </div>
               )}

               <button
                 onClick={handleConnect}
                 className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black shadow-xl hover:scale-[1.01] active:scale-95 transition-all"
               >
                 Confirm Connection
               </button>

               <p className="text-[10px] text-center text-slate-400 italic">
                 Credentials are stored securely and encrypted at rest.
               </p>
            </div>

            <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none" style={{ zIndex: -1 }}>
              {React.createElement(standardConnectors.find(c => c.key === configuring)?.icon || Database, { size: 160 })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
